import React from 'react';
import { Helmet } from 'react-helmet-async';
import ClockDisplay from '../components/ClockDisplay';
import AlarmSettings from '../components/AlarmSettings';
import TimeLeft from '../components/TimeLeft';
import { useFullscreen } from '../contexts/FullscreenContext';
import { getCanonicalUrl } from '../utils/seoUtils';

export default function HomePage({ currentTime, basicAlarm }) {
  const { isFullscreen } = useFullscreen();
  
  const {
    alarmHour,
    alarmMinute,
    snoozeMinutes,
    selectedMelody,
    growing,
    volume,
    alarmActive,
    alarmRinging,
    timeLeft,
    setAlarmHour,
    setAlarmMinute,
    setSnoozeMinutes,
    setSelectedMelody,
    setGrowing,
    setVolume,
    setAlarmActive,
    stopAlarm,
    snoozeAlarm,
    testSound
  } = basicAlarm;

  return (
    <>
      <Helmet>
        <title>Clock Online - Free Alarm Clock</title>
        <meta name="description" content="Free online alarm clock with customizable sounds, snooze function, and growing volume. Set alarms, manage your time, and wake up refreshed each morning." />
        <link rel="canonical" href={getCanonicalUrl('/')} />
        <meta property="og:title" content="Clock Online - Free Alarm Clock" />
        <meta property="og:description" content="Free online alarm clock with customizable sounds, snooze function, and growing volume. Set alarms, manage your time, and wake up refreshed." />
        <meta property="og:url" content={getCanonicalUrl('/')} />
        <meta name="twitter:title" content="Clock Online - Free Alarm Clock" />
        <meta name="twitter:description" content="Free online alarm clock with customizable sounds, snooze function, and growing volume." />
      </Helmet>
      {!isFullscreen && (
        <h1 className="text-4xl font-bold text-white text-center mb-8">
          Clock Online - Free Alarm Clock
        </h1>
      )}
      <ClockDisplay currentTime={currentTime} />
      {!isFullscreen && (
        <>
          <AlarmSettings
            alarmHour={alarmHour}
            alarmMinute={alarmMinute}
            snoozeMinutes={snoozeMinutes}
            selectedMelody={selectedMelody}
            growing={growing}
            volume={volume}
            alarmActive={alarmActive}
            alarmRinging={alarmRinging}
            setAlarmHour={setAlarmHour}
            setAlarmMinute={setAlarmMinute}
            setSnoozeMinutes={setSnoozeMinutes}
            setSelectedMelody={setSelectedMelody}
            setGrowing={setGrowing}
            setVolume={setVolume}
            setAlarmActive={setAlarmActive}
            testSound={testSound}
            stopAlarm={stopAlarm}
            snoozeAlarm={snoozeAlarm}
          />
          {alarmActive && <TimeLeft timeLeft={timeLeft} />}
          
          {/* Usage Instructions - Moved to Bottom */}
          <div className="max-w-4xl mx-auto mt-16 mb-12 px-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-white">
              <h2 className="text-2xl font-semibold mb-6 text-center text-cyan-300">
                Complete Guide to Clock Online Alarms
              </h2>
              
              <div className="space-y-6 text-white/90 leading-relaxed">
                <p className="text-lg">
                  Experience the ultimate convenience with our clock online platform. This comprehensive 
                  alarm system transforms your daily routine through reliable wake-up solutions, advanced 
                  timing features, and personalized settings designed for modern lifestyles.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mt-8">
                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-cyan-300 flex items-center gap-2">
                      🚀 Quick Setup Guide
                    </h3>
                    <p>
                      Setting your clock online alarms is effortless. Select your preferred wake-up time 
                      using intuitive controls, choose from premium sound collections, and activate with 
                      one click. Our clear display ensures you always know when your next alarm will ring.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-cyan-300 flex items-center gap-2">
                      🎵 Premium Sound Library
                    </h3>
                    <p>
                      Choose from carefully selected wake-up sounds ranging from gentle nature melodies 
                      to energizing digital tones. Each sound in our clock online collection is optimized 
                      for pleasant awakening experiences that start your day positively.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-cyan-300 flex items-center gap-2">
                      ⏰ Smart Alarm Features
                    </h3>
                    <p>
                      Our intelligent snooze system provides complete control over your morning routine. 
                      Customize snooze intervals and enable gradual volume increase to ensure reliable 
                      wake-ups without harsh interruptions to your sleep cycle.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-xl font-semibold text-cyan-300 flex items-center gap-2">
                      🌍 Extended Functionality
                    </h3>
                    <p>
                      Beyond basic alarms, explore world clocks, precision timers, meditation sessions, 
                      and multimedia wake-up options. Our platform serves as your complete time management 
                      solution for productivity and wellness.
                    </p>
                  </div>
                </div>

                <div className="bg-white/5 rounded-xl p-6 mt-8">
                  <h3 className="text-xl font-semibold text-cyan-300 mb-4 flex items-center gap-2">
                    💡 Expert Sleep & Wake-Up Tips
                  </h3>
                  <ul className="space-y-3 text-white/80">
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1">•</span>
                      <span>Set clock online alarms 15 minutes early for stress-free morning preparation.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1">•</span>
                      <span>Select nature sounds for gentler awakening that aligns with natural sleep patterns.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1">•</span>
                      <span>Enable gradual volume increase to wake naturally without sudden sound disruption.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1">•</span>
                      <span>Keep devices charged overnight to ensure your alarms function reliably.</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-cyan-400 mt-1">•</span>
                      <span>Test alarm sounds at various volumes to find your optimal wake-up setting.</span>
                    </li>
                  </ul>
                </div>

                <div className="text-center mt-8 p-6 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 rounded-xl">
                  <h3 className="text-xl font-semibold text-cyan-300 mb-3">
                    Start Using Clock Online Today
                  </h3>
                  <p className="text-lg">
                    Join users worldwide who trust our clock online alarms for reliable wake-up solutions. 
                    Set your first alarm above and discover how professional timing tools can enhance 
                    your daily productivity and morning routine.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}